import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Chip, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Typography } from '@mui/material';
import { MultiStatusSelect, SearchControlsLayout, SearchTextField } from '../common';
import { TALK_STATUS_TYPES, TALK_STATUS_STYLES } from '../../types/talk';
import { getPriceOptions } from '../../api/talkRooms';

interface SearchFormProps {
  nurseSearchTerm: string;
  patientSearchTerm: string;
  selectedStatuses: string[];
  selectedPrices: number[];
  totalCount: number;
  onNurseSearchChange: (value: string) => void;
  onPatientSearchChange: (value: string) => void;
  onStatusChange: (value: string[]) => void;
  onPriceChange: (value: number[]) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  onClearNurseSearch: () => void;
  onClearPatientSearch: () => void;
}

const SearchForm: React.FC<SearchFormProps> = ({
  nurseSearchTerm,
  patientSearchTerm,
  selectedStatuses,
  selectedPrices,
  totalCount,
  onNurseSearchChange,
  onPatientSearchChange,
  onStatusChange,
  onPriceChange,
  onSearch,
  onClearFilters,
  onClearNurseSearch,
  onClearPatientSearch,
}) => {
  const [ priceOptions, setPriceOptions ] = useState<{ value: number; label: string }[]>([]);
  // 価格オプションを取得
  useEffect(() => {
    const fetchPriceOptions = async() => {
      const options = await getPriceOptions();
      setPriceOptions(options);
    };
    fetchPriceOptions();
  }, []);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch();
  };
  const handleStatusChange = (event: any) => {
    const value = event.target.value as string[];
    onStatusChange(value);
  };
  return (
    <Paper
      component="form"
      elevation={2}
      sx={{ p: 3, mb: 3 }}
      onSubmit={handleSubmit}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>検索条件</Typography>

      {/* 1列目: ステータス、プラン、看護師名 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <MultiStatusSelect
            label="ステータス"
            id="status-filter"
            value={selectedStatuses}
            onChange={handleStatusChange}
            options={Object.entries(TALK_STATUS_TYPES).map(([value, label]) => {
              const style = TALK_STATUS_STYLES[value];
              return {
                value,
                label,
                chipColor: style?.chipColor,
                customStyle: style?.customStyle
              };
            })}
            emptyLabel="すべて"
            renderChips={true}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="price-filter-label">プラン</InputLabel>
            <Select
              labelId="price-filter-label"
              id="price-filter"
              multiple
              value={selectedPrices}
              onChange={(event) => {
                const value = event.target.value as number[];
                onPriceChange(value);
              }}
              input={<OutlinedInput label="プラン"/>}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>すべて</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((price) => {
                      const option = priceOptions.find(opt => opt.value === price);
                      return (
                        <Chip
                          key={price}
                          label={option?.label || price}
                          size="small"
                          color={price > 0 ? 'error' : 'default'}
                          variant="outlined"
                          sx={{
                            ...(price > 0 ? {
                              fontWeight: 'bold',
                              borderColor: 'error.main',
                              color: 'error.main'
                            } : {})
                          }}
                        />
                      );
                    })}
                  </Box>
                );
              }}
            >
              {priceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={selectedPrices.indexOf(option.value) > -1}
                    style={option.value > 0 ? { color: '#d32f2f' } : undefined}
                  />
                  <ListItemText
                    primary={option.label}
                    style={
                      option.value > 0 && selectedPrices.indexOf(option.value) > -1
                        ? { color: '#d32f2f', fontWeight: 'bold' }
                        : option.value > 0
                          ? { color: '#d32f2f' }
                          : undefined
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <SearchTextField
            label="看護師名 or ニックネーム"
            value={nurseSearchTerm}
            onChange={(e) => onNurseSearchChange(e.target.value)}
            onClear={onClearNurseSearch}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SearchTextField
            label="患者名 or ニックネーム"
            value={patientSearchTerm}
            onChange={(e) => onPatientSearchChange(e.target.value)}
            onClear={onClearPatientSearch}
          />
        </Grid>
      </Grid>

      {/* 2列目: 件数表示、検索ボタン、クリアボタン */}
      <SearchControlsLayout
        count={totalCount}
        onClear={onClearFilters}
      />
    </Paper>
  );
};
export default SearchForm;
